package com.eladfinish.bible.contest.ui.components.dialogs

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Clear
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.ShapeDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.cancel
import com.eladfinish.bible.contest.colorAccent
import com.eladfinish.bible.contest.ok
import com.eladfinish.bible.contest.pdf_email_dialog_email
import com.eladfinish.bible.contest.pdf_email_dialog_first_name
import com.eladfinish.bible.contest.pdf_email_dialog_invalid_email
import com.eladfinish.bible.contest.pdf_email_dialog_last_name
import com.eladfinish.bible.contest.pdf_email_dialog_save_details
import com.eladfinish.bible.contest.pdf_email_dialog_title
import com.eladfinish.bible.contest.ui.layout.heightAboveKeyboard
import dev.icerock.moko.resources.compose.colorResource
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.desc

private val EMAIL_REGEX = Regex("^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$")

/**
 * Demo (POC) dialog: asks the user for their first name, last name and email address,
 * so a demo PDF can be generated and sent to that address.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PdfEmailInputDialog(
    initialFirstName: String = "",
    initialLastName: String = "",
    initialEmail: String = "",
    initialSaveDetails: Boolean = true,
    onDismiss: () -> Unit,
    onConfirm: (firstName: String, lastName: String, email: String, saveDetails: Boolean) -> Unit,
) {
    BasicAlertDialog(
        onDismissRequest = onDismiss,
    ) {
        OutlinedCard(
            modifier = Modifier
                .fillMaxWidth()
                .wrapContentHeight()
                .heightAboveKeyboard(),
            shape = ShapeDefaults.Medium,
            colors = CardDefaults.elevatedCardColors(),
            elevation = CardDefaults.elevatedCardElevation(),
        ) {
            val accentColor = colorResource(SharedRes.colors.colorAccent)
            val firstNameFocusRequester = remember { FocusRequester() }

            var firstName by rememberSaveable { mutableStateOf(initialFirstName) }
            var lastName by rememberSaveable { mutableStateOf(initialLastName) }
            var email by rememberSaveable { mutableStateOf(initialEmail) }
            var saveDetails by rememberSaveable { mutableStateOf(initialSaveDetails) }

            val isConfirmEnabled = firstName.isNotBlank() &&
                    lastName.isNotBlank() &&
                    EMAIL_REGEX.matches(email.trim())

            val submit = {
                if (isConfirmEnabled) {
                    onConfirm(firstName.trim(), lastName.trim(), email.trim(), saveDetails)
                }
            }

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Text(
                    text = stringResource(SharedRes.strings.pdf_email_dialog_title),
                    style = MaterialTheme.typography.titleMedium,
                )

                OutlinedTextField(
                    modifier = Modifier
                        .fillMaxWidth()
                        .focusRequester(firstNameFocusRequester),
                    value = firstName,
                    onValueChange = { firstName = it },
                    textStyle = MaterialTheme.typography.bodyLarge.copy(fontSize = 16.sp),
                    label = {
                        Text(
                            text = stringResource(SharedRes.strings.pdf_email_dialog_first_name),
                            style = MaterialTheme.typography.bodySmall
                        )
                    },
                    trailingIcon = { ClearTextIcon(visible = firstName.isNotEmpty()) { firstName = "" } },
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedLabelColor = accentColor,
                        cursorColor = accentColor,
                        focusedBorderColor = accentColor,
                    ),
                    keyboardOptions = KeyboardOptions.Default.copy(
                        imeAction = ImeAction.Next,
                        keyboardType = KeyboardType.Text,
                    ),
                )

                OutlinedTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = lastName,
                    onValueChange = { lastName = it },
                    textStyle = MaterialTheme.typography.bodyLarge.copy(fontSize = 16.sp),
                    label = {
                        Text(
                            text = stringResource(SharedRes.strings.pdf_email_dialog_last_name),
                            style = MaterialTheme.typography.bodySmall
                        )
                    },
                    trailingIcon = { ClearTextIcon(visible = lastName.isNotEmpty()) { lastName = "" } },
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedLabelColor = accentColor,
                        cursorColor = accentColor,
                        focusedBorderColor = accentColor,
                    ),
                    keyboardOptions = KeyboardOptions.Default.copy(
                        imeAction = ImeAction.Next,
                        keyboardType = KeyboardType.Text,
                    ),
                )

                val emailError = email.isNotBlank() && !EMAIL_REGEX.matches(email.trim())

                OutlinedTextField(
                    modifier = Modifier.fillMaxWidth(),
                    value = email,
                    onValueChange = { email = it },
                    textStyle = MaterialTheme.typography.bodyLarge.copy(fontSize = 16.sp),
                    label = {
                        Text(
                            text = stringResource(SharedRes.strings.pdf_email_dialog_email),
                            style = MaterialTheme.typography.bodySmall
                        )
                    },
                    singleLine = true,
                    isError = emailError,
                    trailingIcon = { ClearTextIcon(visible = email.isNotEmpty()) { email = "" } },
                    supportingText = {
                        if (emailError) {
                            Text(
                                text = stringResource(SharedRes.strings.pdf_email_dialog_invalid_email),
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.error,
                            )
                        }
                    },
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedLabelColor = accentColor,
                        cursorColor = accentColor,
                        focusedBorderColor = accentColor,
                    ),
                    keyboardOptions = KeyboardOptions.Default.copy(
                        imeAction = ImeAction.Done,
                        keyboardType = KeyboardType.Email,
                    ),
                    keyboardActions = KeyboardActions(
                        onDone = { if (isConfirmEnabled) submit() }
                    ),
                )

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable { saveDetails = !saveDetails },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Checkbox(
                        checked = saveDetails,
                        onCheckedChange = { saveDetails = it },
                        colors = CheckboxDefaults.colors(checkedColor = accentColor),
                    )
                    Text(
                        text = stringResource(SharedRes.strings.pdf_email_dialog_save_details),
                        style = MaterialTheme.typography.bodyMedium,
                    )
                }

                Spacer(modifier = Modifier.size(4.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End,
                ) {
                    OutlinedButton(onClick = onDismiss) {
                        Text(SharedRes.strings.cancel.desc().localized())
                    }
                    Spacer(modifier = Modifier.size(12.dp))
                    OutlinedButton(
                        onClick = { submit() },
                        enabled = isConfirmEnabled,
                    ) {
                        Text(SharedRes.strings.ok.desc().localized())
                    }
                }
            }

            LaunchedEffect(Unit) {
                firstNameFocusRequester.requestFocus()
            }
        }
    }
}

/** A trailing "✕" that clears the field; rendered only when there is text to clear. */
@Composable
private fun ClearTextIcon(visible: Boolean, onClear: () -> Unit) {
    if (visible) {
        IconButton(onClick = onClear) {
            Icon(imageVector = Icons.Default.Clear, contentDescription = null)
        }
    }
}

@Preview
@Composable
private fun PdfEmailInputDialogPreview() {
    PdfEmailInputDialog(
        onDismiss = {},
        onConfirm = { _, _, _, _ -> },
    )
}

@Preview
@Composable
private fun PdfEmailInputDialogPrefilledPreview() {
    PdfEmailInputDialog(
        initialFirstName = "ישראל",
        initialLastName = "ישראלי",
        initialEmail = "invalid-email",
        initialSaveDetails = true,
        onDismiss = {},
        onConfirm = { _, _, _, _ -> },
    )
}
